import { useState, type FormEvent } from 'react';
import { color, font } from '../styles/theme';
import { useAuth, ApiError } from '../auth/AuthContext';
import { Button, inputStyle } from './ui';
import { ForgotPassword } from './ForgotPassword';
import { IconSpinner } from './icons';

export function Login() {
  const { signIn, endedReason } = useAuth();
  const [forgot, setForgot] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      // The API answers every failure identically on purpose — do not try to
      // be more specific here than it was.
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (forgot) return <ForgotPassword onBack={() => setForgot(false)} />;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: color.bg,
        padding: 20,
      }}
    >
      <form
        onSubmit={submit}
        className="fade-in"
        style={{
          width: 'min(400px, 100%)',
          background: color.white,
          border: `1px solid ${color.line}`,
          borderRadius: 24,
          padding: 34,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              display: 'flex',
              width: 48,
              height: 48,
              borderRadius: 14,
              background: color.tint,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/enact-logo.png"
              alt=""
              style={{ width: 38, mixBlendMode: 'multiply' }}
            />
          </span>
          <div>
            <h1
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 21,
                color: color.navy,
                margin: 0,
              }}
            >
              ENACT Lab
            </h1>
            <p style={{ fontSize: 13, color: color.muted, margin: '3px 0 0' }}>Admin Dashboard</p>
          </div>
        </div>

        {endedReason && (
          <p
            style={{
              background: color.tint,
              color: color.blueDeep,
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 13,
              fontWeight: 600,
              margin: 0,
            }}
          >
            {endedReason}
          </p>
        )}

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>Email</span>
          <input
            type="email"
            value={email}
            autoComplete="username"
            required
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>Password</span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            required
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </label>

        {error && (
          <p style={{ color: color.red, fontSize: 13, fontWeight: 600, margin: 0 }}>{error}</p>
        )}

        <Button type="submit" disabled={busy} style={{ justifyContent: 'center', marginTop: 4 }}>
          {busy && <IconSpinner size={15} />}
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>

        <button
          type="button"
          onClick={() => setForgot(true)}
          className="hov-blue-text"
          style={{
            background: 'transparent',
            border: 'none',
            color: color.blue,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            padding: 4,
          }}
        >
          Forgot your password?
        </button>

        <p style={{ fontSize: 11.5, color: color.muted, margin: 0, textAlign: 'center' }}>
          This dashboard holds patient information. Sessions end after 30 minutes of inactivity.
        </p>
      </form>
    </div>
  );
}
